import React, { MutableRefObject, ReactNode } from "react";
import styled from "styled-components";

import { trackShareEvent } from "../../../../helpers/analyticsHelpers";
import { getShareUrl } from "../../../../helpers/areenaLinks";
import { writeTextToClipboard } from "../../../../helpers/clipboard";
import i18n from "../../../../service/i18n";
import { usePlayerStore } from "../../../../store/usePlayerStore";
import { focusColor } from "../../../../styles/variables";
import { IconWrapper } from "../../AudioPlayerButtons";

const ItemWrapper = styled.li<{ hoverBackgroundColor: string }>`
  font-size: 12px;

  &:active,
  &:hover {
    background: ${props => props.hoverBackgroundColor};
  }
`;

const Button = styled.button`
  align-items: center;
  background-color: transparent;
  border: transparent;
  border-bottom: 1px solid transparent;
  border-radius: 9px;
  box-shadow: 0 0 0 2px transparent;
  box-sizing: border-box;
  color: inherit;
  cursor: pointer;
  display: flex;
  font: inherit;
  gap: 1rem;
  outline: transparent;
  padding: 0.5rem 1rem;
  width: 100%;
  z-index: 1001;

  &:focus-visible {
    box-shadow: 0 0 0 2px ${focusColor};
  }
`;

const Link = styled.a`
  align-items: center;
  border: 2px solid transparent;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  outline: transparent;
  text-decoration: none;

  &:focus-visible {
    border-radius: 9px;
    border: 2px solid ${focusColor};
  }
`;

const ShareLinkContent = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  gap: 1rem;
  padding: 0.5rem 1rem;
  width: 100%;
`;

const getAudioShareLink = ({
  language,
  programId,
  socialMediaUrl,
  utmSource
}: {
  language: string;
  programId?: string;
  socialMediaUrl?: string;
  utmSource: string;
}) => {
  if (!programId) return "";
  return getShareUrl({
    language,
    programId,
    socialMediaUrl,
    utmSource
  });
};

const ShareItem = ({
  copyLinkClicked,
  copyUrl,
  hoverBackgroundColor,
  icon,
  language,
  source,
  title,
  url
}: {
  copyLinkClicked?: MutableRefObject<boolean>;
  copyUrl?: boolean;
  hoverBackgroundColor: string;
  icon: ReactNode;
  language: string;
  source?: string;
  title: string;
  url?: string;
}) => {
  const { useStoreState } = usePlayerStore();
  const programId = useStoreState.programId();

  const shareAudioLabel = i18n("share_audio", { language });
  const utmSource = source || title.toLowerCase();
  const shareUrl = getAudioShareLink({
    language,
    programId,
    socialMediaUrl: url,
    utmSource: `${utmSource}-share`
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (copyLinkClicked) {
      copyLinkClicked.current = true;
    }
    writeTextToClipboard(getAudioShareLink({ language, programId, utmSource }));
  };

  return (
    <ItemWrapper hoverBackgroundColor={hoverBackgroundColor}>
      {copyUrl ? (
        <Button
          aria-label={`${shareAudioLabel} ${title}`}
          onClick={e => {
            trackShareEvent(`audio-player.share-buttons.${utmSource}`, {
              language
            });

            handleClick(e);
          }}>
          <IconWrapper aria-hidden>{icon}</IconWrapper>

          {title}
        </Button>
      ) : (
        <Link
          aria-label={`${shareAudioLabel} ${title}`}
          target="_blank"
          rel="noopener noreferrer"
          href={shareUrl}
          onClick={() => {
            trackShareEvent(`audio-player.share-buttons.${utmSource}`, {
              language
            });
          }}>
          <ShareLinkContent>
            <IconWrapper aria-hidden>{icon}</IconWrapper>
            {title}
          </ShareLinkContent>
        </Link>
      )}
    </ItemWrapper>
  );
};

export default ShareItem;
